import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { GridCardContent } from "components/atoms";
import { FormikContextType, FormikProvider } from "formik";
import { RoleHelper } from "helpers";
import { isUndefined } from "lodash";
import React, { FC, ForwardedRef } from "react";
import { I18nTranslator, translations } from "utils/i18n";
import FormContent from "./FormContent";

interface IDocumentDetailHeaderProps {
  title: string;
  description?: string;
  formik: FormikContextType<any>;
  loading: boolean;
  renderButtons?: React.ReactNode;
  confirmButtonText?: string;
  confirmButtonIcon?: React.ReactNode;
  hideSearch?: boolean;
}

interface IReportFormContainer {
  formik: FormikContextType<any>;
  onDelete?(): void;
  renderButtons?: React.ReactNode;
  renderConfirmButton?: React.ReactNode;
  confirmButtonText?: string;
}

const ReportHeader: FC<IDocumentDetailHeaderProps> = React.forwardRef(
  (props, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      title,
      description,
      loading,
      formik,
      renderButtons,
      children,
      confirmButtonText,
      confirmButtonIcon,
      hideSearch,
    } = props;

    return (
      <GridCardContent ref={ref}>
        <Grid
          sx={{ width: "100%", padding: "16px" }}
          item
          container
          xs={12}
          alignItems="center"
          rowGap={2}
        >
          <ReportFormContainer formik={formik}>
            <HeaderTitle
              loading={loading}
              title={title}
              description={description}
              confirmButtonText={confirmButtonText}
              renderButtons={renderButtons}
              confirmButtonIcon={confirmButtonIcon}
              hideSearch={hideSearch}
            />
            <FormContent>{children}</FormContent>
          </ReportFormContainer>
        </Grid>
      </GridCardContent>
    );
  }
);
const HeaderTitle = (props: {
  title: string;
  loading: boolean;
  description?: string;
  renderButtons?: React.ReactNode;
  confirmButtonText?: string;
  confirmButtonIcon?: React.ReactNode;
  hideSearch?: boolean;
}) => {
  const {
    title,
    loading,
    description,
    renderButtons,
    confirmButtonText,
    confirmButtonIcon,
    hideSearch,
  } = props;

  return (
    <Grid container item sm={12} sx={{ paddingBottom: "10px" }}>
      <Grid item sm={3}>
        <Stack>
          <Typography
            variant="h2"
            style={{
              color: "black",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h1"
            style={{
              color: "black",
            }}
          >
            {description}
          </Typography>
        </Stack>
      </Grid>
      <FormButtonContainer
        loading={loading}
        renderButtons={renderButtons}
        confirmButtonIcon={confirmButtonIcon}
        confirmButtonText={confirmButtonText}
        hideSearch={hideSearch}
      />
    </Grid>
  );
};

const ReportFormContainer: React.FC<IReportFormContainer> = (props) => {
  const { formik, children } = props;
  return (
    <Box sx={{ bgcolor: "white", width: "100%" }}>
      <FormikProvider value={formik}>
        <form noValidate onSubmit={formik.handleSubmit}>
          <Stack spacing={1}>{children}</Stack>
        </form>
      </FormikProvider>
    </Box>
  );
};

const FormButtonContainer = (props: {
  loading: boolean;
  renderButtons?: React.ReactNode;
  confirmButtonText?: string;
  confirmButtonIcon?: React.ReactNode;
  hideSearch?: boolean;
}) => {
  const {
    loading,
    renderButtons,
    confirmButtonText,
    confirmButtonIcon,
    hideSearch,
  } = props;
  return (
    <Grid
      item
      direction="row"
      display={"flex"}
      justifyContent={"flex-end"}
      sm={9}
    >
      <Box>
        <Stack spacing={2} direction={"row"}>
          {renderButtons}
          {!hideSearch && (
            <Tooltip title={I18nTranslator(translations.button.searchToolTip)}>
              <LoadingButton
                variant="contained"
                type="submit"
                color="primary"
                loading={loading}
                startIcon={confirmButtonIcon || <SearchIcon />}
                disabled={
                  !(
                    RoleHelper.isSuperAdmin() ||
                    RoleHelper.isAdmin() ||
                    RoleHelper.isPlanner()
                  ) && !isUndefined(confirmButtonText)
                }
              >
                {confirmButtonText ||
                  I18nTranslator(translations.button.search)}
              </LoadingButton>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </Grid>
  );
};

export default ReportHeader;
